(function () {
  if (window.customElements.get('bl-cart-line-marker')) return;

  // ----------------------------------------------------------------
  // Visually applies the bundle pricing rule (defined per-position by
  // bartini_marginal_price_N) over server-rendered <line-item> nodes
  // in the drawer. Shopify Discounts handles the actual cart charges;
  // this script only paints the visual: per-item FREE / DISCOUNTED
  // states with strike-through compare prices.
  //
  // Strategy:
  //   1. Sum cart line quantities → totalItemCount.
  //   2. Build a 1-per-position pricing plan from marginalPrices/cap.
  //   3. For each <line-item> (with its qty), expand the corresponding
  //      slice of the plan into contiguous segments. A line whose qty
  //      spans multiple segment types gets visually split into clones.
  // ----------------------------------------------------------------

  var CLONE_ATTR = 'data-bl-marker-clone';
  var DISABLED_ATTR = 'data-bl-marker-disabled';
  var ORIGINAL_HTML_ATTR = 'data-bl-original-html';
  var HIDDEN_COMPARE_ATTR = 'data-bl-hidden-compare';

  var FREE_CLASS = 'bl-cart-line-marker--free';
  var DISCOUNTED_CLASS = 'bl-cart-line-marker--discounted';
  var BADGE_CLASS = 'bl-cart-line-marker__free-badge';
  var COMPARE_CUSTOM_CLASS = 'bl-cart-line-marker__compare-at-price';
  var STRIKE_MARK_CLASS = 'bl-marker-strikethrough';

  var REGULAR_PRICE_CENTS = 4900;

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  function getRoot() {
    return document.querySelector('cart-drawer') || document;
  }

  function formatMoney(cents) {
    var euros = ((parseInt(cents, 10) || 0) / 100).toFixed(2);
    return '€' + euros.replace('.', ',');
  }

  function readMarginalPrices() {
    var bp = document.querySelector('bl-bundle-progress[data-marginal-prices]');
    if (!bp) return null;
    try {
      var arr = JSON.parse(bp.getAttribute('data-marginal-prices'));
      return Array.isArray(arr) ? arr : null;
    } catch (_) {
      return null;
    }
  }

  function readBundleCap() {
    // Fallback hardcoded para 6 — garante que a promoção sempre trava no 6º
    // item mesmo se o data-attribute estiver ausente, vazio ou inválido.
    // Mudar este valor exige edição manual aqui (intencional — proteção contra
    // config quebrada no admin).
    var DEFAULT_CAP = 6;
    var bp = document.querySelector('bl-bundle-progress[data-bundle-cap]');
    if (!bp) return DEFAULT_CAP;
    var n = parseInt(bp.getAttribute('data-bundle-cap'), 10);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_CAP;
  }

  // ----------------------------------------------------------------
  // Pure logic — testable without DOM
  // ----------------------------------------------------------------

  /**
   * Build a per-position pricing plan. plan[i] describes the (i+1)-th
   * item in the cart: its display price, original (compare) price,
   * and whether it's free / discounted.
   *
   * Items past the bundle cap revert to the regular price.
   */
  function computePricingPlan(totalItemCount, marginalPrices, bundleCap) {
    if (!Array.isArray(marginalPrices)) return null;
    var plan = [];
    for (var i = 0; i < totalItemCount; i++) {
      var pastCap = i >= bundleCap;
      var val = (!pastCap && i < marginalPrices.length) ? marginalPrices[i] : REGULAR_PRICE_CENTS;
      var entry;
      if (!pastCap && val === 0) {
        entry = { position: i, displayPrice: 0, originalPrice: REGULAR_PRICE_CENTS, isFree: true, isDiscounted: false };
      } else if (!pastCap && val > 0 && val < REGULAR_PRICE_CENTS) {
        entry = { position: i, displayPrice: val, originalPrice: REGULAR_PRICE_CENTS, isFree: false, isDiscounted: true };
      } else {
        entry = { position: i, displayPrice: REGULAR_PRICE_CENTS, originalPrice: REGULAR_PRICE_CENTS, isFree: false, isDiscounted: false };
      }
      plan.push(entry);
    }
    return plan;
  }

  /**
   * Walk through `qty` consecutive plan entries starting at
   * `globalOffsetStart` and group them into contiguous segments by
   * type. Each segment carries its own qty.
   */
  function planForLine(qty, globalOffsetStart, plan) {
    var segments = [];
    var current = null;
    for (var i = 0; i < qty; i++) {
      var entry = plan[globalOffsetStart + i];
      if (!entry) continue;
      var type = entry.isFree ? 'free' : (entry.isDiscounted ? 'discounted' : 'paid');
      if (current && current.type === type && current.displayPrice === entry.displayPrice) {
        current.qty += 1;
      } else {
        current = { type: type, qty: 1, displayPrice: entry.displayPrice, originalPrice: entry.originalPrice };
        segments.push(current);
      }
    }
    return segments;
  }

  /**
   * Merges segments of the same type+displayPrice into a single segment,
   * preserving the position of the first occurrence. Subsequent occurrences
   * are absorbed and their qty added to the first.
   *
   * Example:
   *   in:  [paid×2, free×2, discounted×1, free×1]
   *   out: [paid×2, free×3, discounted×1]
   */
  function mergeSegmentsByType(segments) {
    var result = [];
    var indexByKey = {};
    for (var i = 0; i < segments.length; i++) {
      var seg = segments[i];
      var key = seg.type + ':' + seg.displayPrice;
      if (Object.prototype.hasOwnProperty.call(indexByKey, key)) {
        result[indexByKey[key]].qty += seg.qty;
      } else {
        indexByKey[key] = result.length;
        result.push({
          type: seg.type,
          qty: seg.qty,
          displayPrice: seg.displayPrice,
          originalPrice: seg.originalPrice
        });
      }
    }
    return result;
  }

  // ----------------------------------------------------------------
  // Component
  // ----------------------------------------------------------------

  class BlCartLineMarker extends HTMLElement {
    constructor() {
      super();
      this._onRefreshed = this._onRefreshed.bind(this);
    }

    connectedCallback() {
      document.addEventListener('cart-drawer:refreshed', this._onRefreshed);
      this._apply();
    }

    disconnectedCallback() {
      document.removeEventListener('cart-drawer:refreshed', this._onRefreshed);
    }

    _onRefreshed() {
      this._apply();
    }

    _apply() {
      var root = getRoot();
      this._cleanup(root);

      var marginalPrices = readMarginalPrices();
      if (!marginalPrices) return;
      var bundleCap = readBundleCap();

      var lines = Array.from(root.querySelectorAll('line-item'))
        .filter(function (el) { return el.getAttribute(CLONE_ATTR) !== 'true'; });

      var totalItemCount = lines.reduce(function (acc, el) {
        var q = parseInt(el.getAttribute('data-line-item-quantity'), 10);
        return acc + (Number.isFinite(q) ? q : 1);
      }, 0);

      if (totalItemCount === 0) return;

      var plan = computePricingPlan(totalItemCount, marginalPrices, bundleCap);
      if (!plan || plan.length === 0) return;

      var cumulativeOffset = 0;
      var self = this;
      lines.forEach(function (line) {
        var qty = parseInt(line.getAttribute('data-line-item-quantity'), 10) || 1;
        var segments = planForLine(qty, cumulativeOffset, plan);
        segments = mergeSegmentsByType(segments);

        if (segments.length === 1) {
          var seg = segments[0];
          if (seg.type === 'free') {
            self._markAsFree(line, qty);
          } else if (seg.type === 'discounted') {
            self._markAsDiscounted(line, qty, seg.displayPrice, seg.originalPrice);
          }
          // 'paid' single-segment: no visual change.
        } else if (segments.length > 1) {
          self._splitLineMulti(line, segments);
        }
        cumulativeOffset += qty;
      });
    }

    // ------------------------------------------------------------
    // Cleanup — must restore the DOM to its server-rendered state
    // before re-applying. Idempotent.
    // ------------------------------------------------------------

    _cleanup(root) {
      // Drop any clones we made in a previous pass.
      root.querySelectorAll('[' + CLONE_ATTR + '="true"]').forEach(function (el) {
        el.remove();
      });

      // Restore line quantities saved before splitting.
      root.querySelectorAll('[data-original-quantity]').forEach(function (el) {
        var original = el.getAttribute('data-original-quantity');
        el.setAttribute('data-line-item-quantity', original);
        el.removeAttribute('data-original-quantity');
        el.removeAttribute('data-bl-segment-qty');
        el.removeAttribute('data-bl-line-total-qty');

        var input = el.querySelector('input[type="number"]');
        if (input) {
          input.value = original;
          input.setAttribute('value', original);
        }
        var text = el.querySelector('[data-quantity-display]');
        if (text) text.textContent = original;
      });

      // Restore <sale-price> innerHTML we overwrote when discounting.
      root.querySelectorAll('sale-price[' + ORIGINAL_HTML_ATTR + ']').forEach(function (el) {
        el.innerHTML = el.getAttribute(ORIGINAL_HTML_ATTR);
        el.removeAttribute(ORIGINAL_HTML_ATTR);
      });

      // Reveal native <compare-at-price> elements we hid.
      root.querySelectorAll('[' + HIDDEN_COMPARE_ATTR + '="true"]').forEach(function (el) {
        el.style.display = '';
        el.removeAttribute(HIDDEN_COMPARE_ATTR);
      });

      // Remove every custom compare-price span we inserted.
      root.querySelectorAll('.' + COMPARE_CUSTOM_CLASS).forEach(function (el) {
        el.remove();
      });

      // Strip FREE marker state.
      root.querySelectorAll('.' + FREE_CLASS).forEach(function (el) {
        el.classList.remove(FREE_CLASS);

        el.querySelectorAll('.' + BADGE_CLASS).forEach(function (b) { b.remove(); });
        el.querySelectorAll('.' + STRIKE_MARK_CLASS).forEach(function (p) {
          p.classList.remove('line-through');
          p.classList.remove(STRIKE_MARK_CLASS);
        });
        el.querySelectorAll('[' + DISABLED_ATTR + '="true"]').forEach(function (t) {
          t.style.pointerEvents = '';
          t.style.opacity = '';
          t.style.cursor = '';
          t.removeAttribute(DISABLED_ATTR);
        });
      });

      // Strip DISCOUNTED marker state.
      root.querySelectorAll('.' + DISCOUNTED_CLASS).forEach(function (el) {
        el.classList.remove(DISCOUNTED_CLASS);
      });
    }

    // ------------------------------------------------------------
    // Marker application
    // ------------------------------------------------------------

    _suppressNativeCompareAtPrice(lineElement) {
      lineElement.querySelectorAll('compare-at-price').forEach(function (el) {
        if (el.classList.contains(COMPARE_CUSTOM_CLASS)) return;
        if (el.getAttribute(HIDDEN_COMPARE_ATTR) === 'true') return;
        el.setAttribute(HIDDEN_COMPARE_ATTR, 'true');
        el.style.display = 'none';
      });
    }

    _markAsFree(lineElement, qty) {
      lineElement.classList.add(FREE_CLASS);
      this._suppressNativeCompareAtPrice(lineElement);

      var salePrice = lineElement.querySelector('sale-price');
      if (salePrice && !salePrice.classList.contains(STRIKE_MARK_CLASS)) {
        salePrice.classList.add('line-through');
        salePrice.classList.add(STRIKE_MARK_CLASS);
      }

      // Inline placement: sibling of <sale-price> inside <price-list>.
      // The previous appendChild on priceList.parentElement put the
      // badge inside the v-stack (column flex) wrapping the title+
      // price, causing it to wrap to a new line below the price.
      var salePriceForBadge = lineElement.querySelector('sale-price');
      var priceList = lineElement.querySelector('price-list');
      if (salePriceForBadge && priceList && !priceList.querySelector('.' + BADGE_CLASS)) {
        var badge = document.createElement('span');
        badge.className = BADGE_CLASS + ' text-subdued h6';
        badge.textContent = 'FREE 🎁';
        salePriceForBadge.insertAdjacentElement('afterend', badge);
      }
    }

    _markAsDiscounted(lineElement, qty, displayPrice, originalPrice) {
      lineElement.classList.add(DISCOUNTED_CLASS);
      this._suppressNativeCompareAtPrice(lineElement);

      var salePrice = lineElement.querySelector('sale-price');
      if (!salePrice) return;

      // Save the server-rendered innerHTML so we can restore it. We
      // overwrite the whole content (preserving any .sr-only label).
      if (!salePrice.hasAttribute(ORIGINAL_HTML_ATTR)) {
        salePrice.setAttribute(ORIGINAL_HTML_ATTR, salePrice.innerHTML);
      }
      var srOnlyHtml = '';
      var srOnly = salePrice.querySelector('.sr-only');
      if (srOnly) srOnlyHtml = srOnly.outerHTML;
      salePrice.innerHTML = srOnlyHtml + formatMoney(displayPrice);

      // Sibling compare-at-price next to the sale-price.
      var priceList = lineElement.querySelector('price-list');
      if (priceList && !priceList.querySelector('.' + COMPARE_CUSTOM_CLASS)) {
        var compare = document.createElement('span');
        compare.className = COMPARE_CUSTOM_CLASS;
        compare.textContent = formatMoney(originalPrice);
        salePrice.insertAdjacentElement('beforebegin', compare);
      }
    }

    // ------------------------------------------------------------
    // Multi-segment split: original line + (segments.length - 1) clones
    // ------------------------------------------------------------

    _splitLineMulti(lineElement, segments) {
      if (!lineElement.hasAttribute('data-original-quantity')) {
        var current = lineElement.getAttribute('data-line-item-quantity');
        lineElement.setAttribute('data-original-quantity', current);
      }

      // Calcula totalQty somando segments (mais robusto que ler
      // data-original-quantity em caso de re-split).
      var totalQty = 0;
      for (var s = 0; s < segments.length; s++) {
        totalQty += segments[s].qty;
      }

      // First segment lives on the original element.
      var first = segments[0];
      this._setDisplayedQuantity(lineElement, first.qty);
      lineElement.setAttribute('data-line-item-quantity', String(first.qty));
      lineElement.setAttribute('data-bl-segment-qty', String(first.qty));
      lineElement.setAttribute('data-bl-line-total-qty', String(totalQty));
      this._applySegment(lineElement, first);

      // Each remaining segment is a fresh clone inserted in order.
      var prevElement = lineElement;
      for (var i = 1; i < segments.length; i++) {
        var seg = segments[i];
        var clone = this._cloneCleanLine(lineElement);
        clone.setAttribute(CLONE_ATTR, 'true');
        clone.setAttribute('data-line-item-quantity', String(seg.qty));
        clone.setAttribute('data-bl-segment-qty', String(seg.qty));
        clone.setAttribute('data-bl-line-total-qty', String(totalQty));
        this._setDisplayedQuantity(clone, seg.qty);
        this._applySegment(clone, seg);

        prevElement.insertAdjacentElement('afterend', clone);
        prevElement = clone;
      }
    }

    /**
     * Clone a line element and strip any marker state that copied
     * over from the source. Resets sale-price innerHTML if it was
     * overwritten in this pass.
     */
    _cloneCleanLine(sourceLine) {
      var clone = sourceLine.cloneNode(true);
      clone.removeAttribute('data-original-quantity');
      clone.removeAttribute('data-bl-segment-qty');
      clone.removeAttribute('data-bl-line-total-qty');
      clone.classList.remove(FREE_CLASS);
      clone.classList.remove(DISCOUNTED_CLASS);

      clone.querySelectorAll('.' + BADGE_CLASS).forEach(function (b) { b.remove(); });
      clone.querySelectorAll('.' + COMPARE_CUSTOM_CLASS).forEach(function (c) { c.remove(); });
      clone.querySelectorAll('.' + STRIKE_MARK_CLASS).forEach(function (p) {
        p.classList.remove('line-through');
        p.classList.remove(STRIKE_MARK_CLASS);
      });
      clone.querySelectorAll('sale-price[' + ORIGINAL_HTML_ATTR + ']').forEach(function (el) {
        el.innerHTML = el.getAttribute(ORIGINAL_HTML_ATTR);
        el.removeAttribute(ORIGINAL_HTML_ATTR);
      });
      clone.querySelectorAll('[' + HIDDEN_COMPARE_ATTR + '="true"]').forEach(function (el) {
        el.style.display = '';
        el.removeAttribute(HIDDEN_COMPARE_ATTR);
      });
      clone.querySelectorAll('[' + DISABLED_ATTR + '="true"]').forEach(function (t) {
        t.style.pointerEvents = '';
        t.style.opacity = '';
        t.style.cursor = '';
        t.removeAttribute(DISABLED_ATTR);
      });

      return clone;
    }

    _applySegment(lineElement, segment) {
      if (segment.type === 'free') {
        this._markAsFree(lineElement, segment.qty);
      } else if (segment.type === 'discounted') {
        this._markAsDiscounted(lineElement, segment.qty, segment.displayPrice, segment.originalPrice);
      }
      // 'paid' segments leave the server-rendered price untouched.
    }

    _setDisplayedQuantity(element, qty) {
      var input = element.querySelector('input[type="number"]');
      if (input) {
        input.value = String(qty);
        input.setAttribute('value', String(qty));
      }
      var text = element.querySelector('[data-quantity-display]');
      if (text) text.textContent = String(qty);
    }
  }

  window.customElements.define('bl-cart-line-marker', BlCartLineMarker);
})();
